import { useState, useEffect } from 'react';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { ImageLightbox } from '../ui/ImageLightbox';
import type { Exercise } from '../../types';
import type { TemplateItemDraft } from './TemplateBuilder';

interface TemplateItemDrawerProps {
  isOpen: boolean;
  onClose: (updatedItem: TemplateItemDraft) => void;
  item: TemplateItemDraft | null;
  exercise: Exercise | null;
  onDelete: () => void;
}

/**
 * Drawer for editing template item parameters (sets, reps, rest, weight, cues)
 * Changes auto-save to local template state when drawer closes.
 */
export function TemplateItemDrawer({
  isOpen,
  onClose,
  item,
  exercise,
  onDelete,
}: TemplateItemDrawerProps) {
  // Local state for form fields
  const [sets, setSets] = useState<number>(3);
  const [reps, setReps] = useState<number>(8);
  const [restSec, setRestSec] = useState<number>(90);
  const [weightSuggestion, setWeightSuggestion] = useState<string>('');
  const [customCues, setCustomCues] = useState<string>('');
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Sync local state when item changes
  useEffect(() => {
    if (item) {
      setSets(item.sets ?? 3);
      setReps(item.reps ?? 8);
      setRestSec(item.restSec ?? 90);
      setWeightSuggestion(item.weightSuggestion ?? '');
      setCustomCues(item.customCues ?? '');
    }
  }, [item]);

  const handleClose = () => {
    if (item) {
      onClose({
        exerciseId: item.exerciseId,
        sets,
        reps,
        restSec,
        weightSuggestion,
        customCues,
      });
    }
  };

  const handleDelete = () => {
    onDelete();
  };

  if (!item || !exercise) return null;

  return (
    <Panel
      isOpen={isOpen}
      onClose={handleClose}
      title={exercise.name}
      variant="drawer"
    >
      <div className="space-y-4">
        {/* Exercise thumbnail/info */}
        {exercise.media && exercise.media.length > 0 && exercise.media[0].type === 'image' && (
          <>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="w-full h-32 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/5 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img
                src={exercise.media[0].downloadUrl || exercise.media[0].externalUrl || ''}
                alt={exercise.name}
                className="w-full h-full object-cover"
              />
            </button>
            <ImageLightbox
              src={exercise.media[0].downloadUrl || exercise.media[0].externalUrl || ''}
              alt={exercise.name}
              isOpen={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
            />
          </>
        )}

        {/* Sets & Reps row */}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span className="text-xs uppercase tracking-wide text-slate-400">Sets</span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field text-lg font-medium"
              value={sets}
              onChange={(e) => setSets(Number(e.target.value) || 0)}
            />
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span className="text-xs uppercase tracking-wide text-slate-400">Reps</span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field text-lg font-medium"
              value={reps}
              onChange={(e) => setReps(Number(e.target.value) || 0)}
            />
          </label>
        </div>

        {/* Rest & Weight row */}
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span className="text-xs uppercase tracking-wide text-slate-400">Rest (sec)</span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-field"
              value={restSec}
              onChange={(e) => setRestSec(Number(e.target.value) || 0)}
            />
          </label>
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span className="text-xs uppercase tracking-wide text-slate-400">Weight</span>
            <input
              type="text"
              className="form-field"
              placeholder="e.g. RPE 7, 20lb"
              value={weightSuggestion}
              onChange={(e) => setWeightSuggestion(e.target.value)}
            />
          </label>
        </div>

        {/* Custom cues */}
        <label className="flex flex-col gap-2 text-sm text-slate-300">
          <span className="text-xs uppercase tracking-wide text-slate-400">Custom cues</span>
          <textarea
            className="form-textarea max-w-none min-h-[100px]"
            placeholder="Notes for this client on this movement"
            value={customCues}
            onChange={(e) => setCustomCues(e.target.value)}
          />
        </label>

        {/* Default cues from exercise (read-only reference) */}
        {exercise.defaultCues && (
          <div className="rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Default cues (reference)</p>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{exercise.defaultCues}</p>
          </div>
        )}

        {/* Delete button */}
        <Button variant="danger" onClick={handleDelete} className="w-full">
          Remove from template
        </Button>
      </div>
    </Panel>
  );
}
